import moment from 'moment';
import type { AggregateDef } from '../schema/types';

export const DATE_COLUMN_TYPES = new Set([
  'date',
  'dateTime',
  'dateTimeLTS',
  'dateRange',
  'conditionalDate',
]);

export function isDateColumnType(columnType: string | undefined): boolean {
  return columnType != null && DATE_COLUMN_TYPES.has(columnType);
}

export function normaliseDateToGroupKey(value: unknown): string {
  if (value == null) return '__null__';

  const m = moment(value as string | number | Date);
  if (m.isValid()) {
    return m.format('YYYY-MM-DD');
  }

  const raw = String(value).trim();
  return raw === '' ? '__null__' : raw;
}

export function isGroupRow(row: unknown): row is { __isGroupRow: true } {
  return (
    row != null &&
    typeof row === 'object' &&
    '__isGroupRow' in row &&
    (row as { __isGroupRow: boolean }).__isGroupRow === true
  );
}

export function calculateAggregate(
  rows: Record<string, unknown>[],
  field: string,
  aggregateDef: AggregateDef,
): unknown {
  const values = rows
    .map((row) => row[field])
    .filter((v) => v != null && typeof v === 'number') as number[];

  if (values.length === 0) return null;

  switch (aggregateDef.fn) {
    case 'sum':
      return values.reduce((acc, v) => acc + v, 0);
    case 'average':
      return values.reduce((acc, v) => acc + v, 0) / values.length;
    case 'count':
      return values.length;
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    default:
      return null;
  }
}

export function formatCanonicalDateKey(key: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
    const [yyyy, mm, dd] = key.split('-');
    return `${mm}/${dd}/${yyyy}`;
  }
  if (key === '__null__') return '(Empty)';
  return key;
}

export function formatGroupValue(value: unknown, columnType?: string): string {
  if (value == null) return '(Empty)';

  if (isDateColumnType(columnType)) {
    return formatCanonicalDateKey(String(value));
  }

  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) {
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    const year = value.getFullYear();
    return `${month}/${day}/${year}`;
  }
  return String(value);
}
